import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { healthCyclesAPI } from '../services/api'
import CycleStateCard from '../components/health-cycle/CycleStateCard'
import StartCycleForm from '../components/health-cycle/StartCycleForm'
import UntilNextTestSection from '../components/progress/UntilNextTestSection'
import WhatChangedCard from '../components/home/WhatChangedCard'
import TodayCard from '../components/home/TodayCard'
import CheckpointCard from '../components/home/CheckpointCard'

/**
 * VH-247 stage 2: the health cycle is the home page (it replaces the Dashboard at "/").
 * One screen answers "am I on plan, and what changed since the last blood test":
 * where I stand and the next step, what changed, the agreed actions so far, and today.
 */
export default function CycleHome() {
  const { t } = useTranslation()
  const stateQuery = useQuery({
    queryKey: ['healthCycleState'],
    queryFn: () => healthCyclesAPI.currentState().then((response) => response.data),
  })
  const state = stateQuery.data
  const startLeads = state?.state === 'reviewing' && state.review === 'not_started'
  const agreedInForce = state && (state.state === 'doing' || state.state === 'measuring')

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-3xl" data-testid="cycle-home">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('home.title')}</h1>

      {stateQuery.isLoading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
      {stateQuery.isError && (
        <div className="card">
          <div className="card-body text-sm">
            <p className="text-red-600">{t('healthCycle.state.loadError')}</p>
            <button type="button" className="btn-outline mt-2 text-xs" onClick={() => stateQuery.refetch()}>
              {t('healthCycle.state.retry')}
            </button>
          </div>
        </div>
      )}
      {state && (
        <CycleStateCard
          state={state}
          onStartWaiting={startLeads ? <StartCycleForm bare initialTestId={state.waiting_test.blood_test_id} /> : null}
        />
      )}
      {state?.agreed_cycle && <WhatChangedCard agreed={state.agreed_cycle} />}
      {agreedInForce && <CheckpointCard />}
      {agreedInForce && <UntilNextTestSection />}
      <TodayCard />

      <p className="text-sm">
        <Link to="/dashboard" className="text-primary-600 hover:text-primary-700">{t('home.allFigures')}</Link>
      </p>
    </div>
  )
}
